// recipeDiff — field-level diff between two recipe objects (schema.js
// shape). Pure, no React, no I/O.
//
// Three consumers:
//   • RecipeReviseSheet's review panel — "cayenne · 1 tsp → ¼ tsp",
//     "+ honey", "Step 3 — reworked" — the consent gate before an AI
//     revision saves.
//   • buildForkPayload — summarizeDiff(diff) stamps compact tweak
//     counts into forkedFrom.tweaks so the recipe page can render the
//     celebratory "{n} TWEAKS OF MINE" chip without retaining the
//     whole original recipe.
//   • describeDiff — short human strings for chips/toasts.
//
// Keying rules: ingredients match by ingredientId when both sides
// carry one, else by normalized item text — so an amount tweak on
// "sharp cheddar" reads as a CHANGE, not a remove+add. Steps match by
// step.id when present (AI recipes carry ids), else by index.

function normalizeName(s) {
  return String(s || "").trim().toLowerCase();
}

function ingredientKey(ing, idx) {
  if (ing?.ingredientId) return `id:${ing.ingredientId}`;
  const n = normalizeName(ing?.item || ing?.name);
  return n ? `name:${n}` : `idx:${idx}`;
}

function stepKey(step, idx) {
  return step?.id ? `id:${step.id}` : `idx:${idx}`;
}

function normText(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

/**
 * @returns {{
 *   ingredients: {
 *     added:   Array<{item, amount}>,
 *     removed: Array<{item, amount}>,
 *     changed: Array<{item, fromAmount, toAmount}>,
 *   },
 *   steps: {
 *     added:   Array<{index, title}>,
 *     removed: Array<{index, title}>,
 *     changed: Array<{index, title, titleChanged, instructionChanged}>,
 *   },
 *   serves: {from, to} | null,
 *   title:  {from, to} | null,
 * }}
 */
export function diffRecipes(before, after) {
  const out = {
    ingredients: { added: [], removed: [], changed: [] },
    steps: { added: [], removed: [], changed: [] },
    serves: null,
    title: null,
  };
  const b = before || {};
  const a = after || {};

  // ── ingredients ──────────────────────────────────────────────────
  const bIngs = Array.isArray(b.ingredients) ? b.ingredients : [];
  const aIngs = Array.isArray(a.ingredients) ? a.ingredients : [];
  const bByKey = new Map(bIngs.map((ing, i) => [ingredientKey(ing, i), ing]));
  const aByKey = new Map(aIngs.map((ing, i) => [ingredientKey(ing, i), ing]));

  for (const [key, ing] of aByKey) {
    const prev = bByKey.get(key);
    if (!prev) {
      out.ingredients.added.push({
        item: ing.item || ing.name || "ingredient",
        amount: ing.amount || null,
      });
      continue;
    }
    const fromAmount = normText(prev.amount);
    const toAmount = normText(ing.amount);
    const fromItem = normText(prev.item || prev.name);
    const toItem = normText(ing.item || ing.name);
    if (fromAmount !== toAmount || fromItem !== toItem) {
      out.ingredients.changed.push({
        item: ing.item || ing.name || prev.item || "ingredient",
        fromAmount: prev.amount || null,
        toAmount: ing.amount || null,
      });
    }
  }
  for (const [key, ing] of bByKey) {
    if (!aByKey.has(key)) {
      out.ingredients.removed.push({
        item: ing.item || ing.name || "ingredient",
        amount: ing.amount || null,
      });
    }
  }

  // ── steps ────────────────────────────────────────────────────────
  const bSteps = Array.isArray(b.steps) ? b.steps : [];
  const aSteps = Array.isArray(a.steps) ? a.steps : [];
  const bStepByKey = new Map(bSteps.map((s, i) => [stepKey(s, i), { step: s, index: i }]));
  const aStepByKey = new Map(aSteps.map((s, i) => [stepKey(s, i), { step: s, index: i }]));

  for (const [key, { step, index }] of aStepByKey) {
    const prev = bStepByKey.get(key);
    if (!prev) {
      out.steps.added.push({ index, title: step.title || null });
      continue;
    }
    const titleChanged = normText(prev.step.title) !== normText(step.title);
    const instructionChanged = normText(prev.step.instruction) !== normText(step.instruction);
    if (titleChanged || instructionChanged) {
      out.steps.changed.push({
        index,
        title: step.title || prev.step.title || null,
        titleChanged,
        instructionChanged,
      });
    }
  }
  for (const [key, { step, index }] of bStepByKey) {
    if (!aStepByKey.has(key)) {
      out.steps.removed.push({ index, title: step.title || null });
    }
  }

  // ── scalars ──────────────────────────────────────────────────────
  if (Number(b.serves) !== Number(a.serves)
      && (Number.isFinite(Number(b.serves)) || Number.isFinite(Number(a.serves)))) {
    out.serves = { from: b.serves ?? null, to: a.serves ?? null };
  }
  if (normText(b.title) !== normText(a.title)) {
    out.title = { from: b.title || null, to: a.title || null };
  }

  return out;
}

// Compact counts for forkedFrom.tweaks — small enough to live in the
// JSONB forever, rich enough for the "{n} TWEAKS OF MINE" chip.
export function summarizeDiff(diff) {
  if (!diff) return null;
  const counts = {
    changed: diff.ingredients.changed.length,
    added: diff.ingredients.added.length,
    removed: diff.ingredients.removed.length,
    stepsChanged: diff.steps.changed.length + diff.steps.added.length + diff.steps.removed.length,
  };
  counts.total = counts.changed + counts.added + counts.removed + counts.stepsChanged
    + (diff.serves ? 1 : 0);
  return counts;
}

// Short human strings for chips / review rows. Capped so a sweeping
// AI revision doesn't render a wall.
export function describeDiff(diff, { max = 8 } = {}) {
  if (!diff) return [];
  const lines = [];
  for (const c of diff.ingredients.changed) {
    lines.push(`${c.item} · ${c.fromAmount || "—"} → ${c.toAmount || "—"}`);
  }
  for (const aIng of diff.ingredients.added) {
    lines.push(`+ ${aIng.item}${aIng.amount ? ` · ${aIng.amount}` : ""}`);
  }
  for (const r of diff.ingredients.removed) {
    lines.push(`− ${r.item}`);
  }
  for (const s of diff.steps.changed) {
    lines.push(`Step ${s.index + 1}${s.title ? ` · ${s.title}` : ""} — reworked`);
  }
  for (const s of diff.steps.added) {
    lines.push(`+ Step ${s.index + 1}${s.title ? ` · ${s.title}` : ""}`);
  }
  for (const s of diff.steps.removed) {
    lines.push(`− Step ${s.index + 1}${s.title ? ` · ${s.title}` : ""}`);
  }
  if (diff.serves) lines.push(`Serves ${diff.serves.from ?? "—"} → ${diff.serves.to ?? "—"}`);
  if (lines.length > max) {
    const extra = lines.length - max;
    return [...lines.slice(0, max), `+ ${extra} more change${extra === 1 ? "" : "s"}`];
  }
  return lines;
}
